import {useEffect, useRef} from "react";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import * as Constants from "../utils/Constants";

/**
 * Subscribes to live Scheduled Group Unlock updates — session created/cancelled, each per-lock
 * attempt outcome, and session completion — broadcast on Constants.TOPIC_SCHEDULED_UNLOCKS.
 * Same STOMP/SockJS pattern as DeviceCommandCenter's command-response subscription.
 *
 * Connects only while `enabled` is true (e.g. a slide-over being open) and disconnects on
 * unmount/disable. `onUpdate` receives the raw ScheduledUnlockSessionDto for every broadcast —
 * callers filter by `id` themselves since this is a single shared topic, not per-session.
 */
export default function useScheduledUnlockUpdates(onUpdate, enabled = true) {
  const stompClientRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!enabled) return undefined;

    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const client = Stomp.over(socket);
    client.debug = null; // suppress STOMP debug logs

    client.connect({}, () => {
      client.subscribe(Constants.TOPIC_SCHEDULED_UNLOCKS, (msg) => {
        try {
          onUpdateRef.current(JSON.parse(msg.body));
        } catch (e) {
          console.error("Error parsing scheduled-unlock update:", e);
        }
      });
      stompClientRef.current = client;
    });

    return () => {
      if (stompClientRef.current?.connected) {
        stompClientRef.current.disconnect();
      }
      stompClientRef.current = null;
    };
  }, [enabled]);
}
